import { DepositTypeEnum, TransactionTypeEnum, WithdrawTypeEnum } from "@payping/api";
import type { TransactionDetails } from "@payping/api";
import { getCashOutOverview, getCashOutTransactions } from "@payping/redux/actions";
import qs from "query-string";
import { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAsync } from "react-use";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { submitQueryParams } from "@payping/utils/query-string";
import { FiltersType, GridifyConditionEnum, GridifyLogicalEnum, createQuery } from "@payping/utils/api";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { Flex, Space, Table, Tag, Typography, useMessagedAsyncRetry } from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useCopy } from "@/utils";
import type { ColumnsType, TablePaginationConfig } from "@payping/web-components/components/table/types";
import styles from "@/assets/scss/Settlement/index.module.scss";
import Filters from "./ListFilters";
import TransactionDownloadList from "./DownloadList";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

const filterGenerator = ({
  transactionType,
  depositType,
  withdrawType,
  createdOnFrom,
  createdOnTo,
}: {
  transactionType?: number;
  depositType?: number;
  withdrawType?: number;
  createdOnFrom?: string;
  createdOnTo?: string;
}): FiltersType => [
  {
    transactionType: [GridifyConditionEnum.Equal, transactionType],
    depositType: [GridifyConditionEnum.Equal, depositType],
    withdrawType: [GridifyConditionEnum.Equal, withdrawType],
    createdOn: [GridifyConditionEnum.GreaterThan, createdOnFrom],
  },
  GridifyLogicalEnum.And,
  { createdOn: [GridifyConditionEnum.LessThan, createdOnTo] },
];

const Transaction: FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const parsedSearch = qs.parse(location.search);
  const {
    pageNumber = 1,
    pageSize = 10,
    createdOnFrom,
    createdOnTo,
    depositType,
    transactionType,
    withdrawType,
  }: {
    pageNumber?: number;
    pageSize?: number;
    transactionType?: number;
    depositType?: number;
    withdrawType?: number;
    createdOnFrom?: string;
    createdOnTo?: string;
  } = qs.parse(location.search, { parseNumbers: true });
  const transactionList = useAppSelector((state) => state.cashOuts.depositTransactionsList);
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { handleCopy } = useCopy();

  useAsync(() => dispatch(getCashOutOverview()));

  const { loading, retry } = useMessagedAsyncRetry(
    () =>
      dispatch(
        getCashOutTransactions(
          pageNumber,
          pageSize,
          undefined,
          createQuery(filterGenerator({ createdOnFrom, createdOnTo, depositType, transactionType, withdrawType })),
        ),
      ),
    [dispatch, pageSize, pageNumber, transactionType, depositType, withdrawType, createdOnFrom, createdOnTo],
    { success: false, useProgress: true },
  );

  useAsync(() => dispatch(getCashOutOverview()));

  const columns: ColumnsType<TransactionDetails> = [
    {
      dataIndex: "createdOn",
      key: "createdOn",
      render(value?: string) {
        return <span>{toJalali({ date: value, toFormat: dateFormats.ui.jalaliDateTime }).string}</span>;
      },
      title: t("date"),
    },
    {
      dataIndex: "amount",
      render(value?: number) {
        return (
          <Flex align="center">
            <Typography.Text>{`${formatLocalizedNumber({ value: value ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("amount"),
    },
    {
      dataIndex: "ref",
      render(value?: string) {
        return <Typography.Text>{value}</Typography.Text>;
      },
      title: t("settlement.transaction.ref"),
      onCell: (record) => ({
        onClick: () => {
          handleCopy(record.ref);
        },
      }),
    },
    {
      dataIndex: "transactionType",
      render(value: TransactionTypeEnum) {
        switch (value) {
          case TransactionTypeEnum.Deposit:
            return (
              <Tag color="success">
                <Typography.Text>{t("common.deposit")}</Typography.Text>
              </Tag>
            );
          case TransactionTypeEnum.Withdraw:
            return (
              <Tag color="error">
                <Typography.Text>{t("common.withdraw")}</Typography.Text>
              </Tag>
            );
          default:
            return null;
        }
      },
      title: t("settlement.transaction.deposit/withdraw"),
    },
    {
      dataIndex: "transactionType",
      render(value: TransactionTypeEnum, record: TransactionDetails) {
        if (value === TransactionTypeEnum.Deposit) {
          return typeof record.depositType === "number"
            ? t(`settlement.transaction.depositType.${DepositTypeEnum[record.depositType]}`)
            : null;
        }
        if (value === TransactionTypeEnum.Withdraw) {
          return typeof record.withdrawType === "number"
            ? t(`settlement.transaction.withdrawType.${WithdrawTypeEnum[record.withdrawType]}`)
            : null;
        }
        return null;
      },
      title: t("settlement.transaction.transactionType"),
    },
    {
      dataIndex: "description",
      render(value?: string) {
        return <Typography.Text>{value}</Typography.Text>;
      },
      title: t("settlement.transaction.description"),
    },
    {
      dataIndex: "balance",
      render(value?: number) {
        return (
          <Flex align="center">
            <Typography.Text>{`${formatLocalizedNumber({ value: value ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("settlement.transaction.balance"),
    },
  ];

  const pagination: TablePaginationConfig = {
    onChange: (page, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: page,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry,
      });
    },
    onShowSizeChange: (_currentPage, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: 1,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry,
      });
    },
    pageNumber,
    pageSize,
    showTotal: (total, range): string => t("showTotal", { range: `${range[0]}-${range[1]}`, total }),
    total: transactionList?.count,
  };

  return (
    <Space orientation="vertical" className="tw:w-full">
      <Filters
        key={0}
        loading={loading}
        retry={() => retry()}
        DownloadExcel={
          <TransactionDownloadList
            key={1}
            loading={loading}
            filters={createQuery(
              filterGenerator({ createdOnFrom, createdOnTo, depositType, transactionType, withdrawType }),
            )}
          />
        }
      />

      <Table<TransactionDetails>
        hasIdColumn
        className={styles.tableWrapper}
        dataSource={transactionList?.data}
        columns={columns}
        pagination={pagination}
        loading={loading}
      />
    </Space>
  );
};

export default Transaction;
